import { useEffect, useState } from 'react';
import {
  captureAction,
  facilityReimbursementPortal,
  feeSchedule,
  myReimbursementPortal,
  portfolioReimbursementPortal,
  type CaptureActionKey,
  type PortalLiveProgram,
  type PortalReadinessRow,
  type RebatesBasis,
  type ReimbursementPortal,
} from '@aithora/shared';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../../session/SessionContext';
import { sx } from '../../ui/designStyles';
import { READ_ONLY_SHORT } from '../../ui/ReadOnly';
import { usePerf } from './ctx';
import { FIXABLE, FixSheet } from './FixSheet';
import { notServed, pre, usePerfApi } from './lib';
import { BuildTagged, useCustomerText } from '../../ui/buildLabels';

// The design's palette for this tab (rbVals, L44341).
const INK = '#22272E';
const GREY = '#54606C';
const GREEN = '#1F6B52';
const RED = '#B3261E';
const BLUE = '#2F5C97';
const SOFT = '#3C454F';
const k$ = (n: number) => (n >= 1000 ? '$' + (n / 1000).toFixed(1) + 'K' : '$' + Math.round(n));
const chip = (c: string, bg: string) => 'font-size:14px;font-weight:700;letter-spacing:.04em;padding:0 5px;border-radius:3px;white-space:nowrap;color:' + c + ';background:' + bg + ';';
const DASH = chip(GREY, 'transparent') + 'border:1px dashed rgba(34,39,46,.30);';
const OURS = chip(GREEN, 'rgba(47,138,111,.11)');
const CARD = 'box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:7px 0 0;min-width:0;';
const CARD_HEAD = 'font-size:16px;font-weight:800;letter-spacing:-.01em;color:' + INK + ';white-space:nowrap;';
const NOTE = 'font-size:14px;color:' + GREY + ';line-height:1.3;margin-top:3px;text-wrap:pretty;';
const reg = (cols: string) => 'display:grid;grid-template-columns:' + cols + ';gap:7px;align-items:baseline;padding:2px 0;min-width:0;border-top:1px solid rgba(34,39,46,.07);';
const num = (c: string, w = 14) => 'text-align:right;font-size:' + w + 'px;font-weight:800;font-variant-numeric:tabular-nums;white-space:nowrap;color:' + c + ';';
const K_STYLE = 'min-width:0;font-size:14px;color:' + SOFT + ';line-height:1.25;';

/** The word the design prints for a basis: our own record is OURS. */
const WORD: Record<RebatesBasis, string> = { measured: 'OURS', modeled: 'MODELED', part_modeled: 'PART MODEL' };
const tagOf = (b: RebatesBasis | undefined) => (b ? { tag: WORD[b], style: b === 'measured' ? OURS : DASH } : null);

const seg = (v: number, c: string, lab: string, dark?: boolean) => ({
  segStyle: 'flex:' + Math.max(1, Math.round(v)) + ' 1 0;min-width:0;display:flex;align-items:center;justify-content:center;background:' + c + ';animation:growW .6s cubic-bezier(.22,.61,.36,1);transform-origin:left;',
  labStyle: 'font-size:14px;font-weight:800;white-space:nowrap;overflow:hidden;padding:0 4px;color:' + (dark ? INK : '#FCFAF4') + ';',
  lab,
});

/** Which capture action a readiness fix records (the portal's action routes). */
const ACTION: Record<string, CaptureActionKey> = {
  consent: 'send_consent',
  election_consent: 'send_consent',
  care_plan: 'open_care_plan',
  care_plan_goals: 'open_care_plan',
  companion_visit: 'book_visit',
  chronic_dx: 'fix_code',
  credential: 'start_enrollment',
};
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Whole months since go-live. */
function monthsLive(goLive: string, now: number): number {
  const g = new Date(goLive + (goLive.length === 10 ? 'T12:00:00' : ''));
  const n = new Date(now);
  let m = (n.getFullYear() - g.getFullYear()) * 12 + n.getMonth() - g.getMonth();
  if (n.getDate() < g.getDate()) m -= 1;
  return Math.max(0, m);
}

/**
 * Rebates / Reimbursement (design isRebates, T16837-17076): what changed because Aithora
 * is here (prior, recovered, created, still on the table; never added together), the
 * money this period, who is billable, the claims, each program, the readiness work list,
 * the surfaces that are not a billing rail, and the capture curve since go-live. All from
 * the facility reimbursement portal.
 */
export function RebatesTab() {
  const { fac } = usePerf();
  const [scope, setScope] = useState<'bldg' | 'portfolio'>('bldg');
  // keyed, so a new scope or building never shows the last one's figures while it loads
  return <RebatesBody key={scope + (fac?.orgId ?? '')} scope={scope} setScope={setScope} />;
}

function RebatesBody({ scope, setScope }: { scope: 'bldg' | 'portfolio'; setScope: (s: 'bldg' | 'portfolio') => void }) {
  const plain = useCustomerText();
  const { op, fa, canAct, roReason, fac } = usePerf();
  const { client } = useSession();
  const navigate = useNavigate();
  // What was cleared, by resident and gate (not by row position): after a fix the list
  // reloads and the same resident's NEXT gate takes that row, which must stay workable.
  const [done, setDone] = useState<Record<string, boolean>>({});
  const doneKey = (w: { residentId: string; blocker: string }) => w.residentId + ':' + w.blocker;
  const [err, setErr] = useState<string | null>(null);
  const [fixFor, setFixFor] = useState<{ i: number; row: PortalReadinessRow } | null>(null);
  const orgId = fac?.orgId ?? null;
  const portfolio = op && scope === 'portfolio';
  const q = usePerfApi<ReimbursementPortal>(
    (c, signal) =>
      portfolio
        ? portfolioReimbursementPortal(c, { signal })
        : fa
          ? myReimbursementPortal(c, { signal })
          : facilityReimbursementPortal(c, { params: { id: orgId! }, signal }),
    [portfolio, fa, orgId],
    fa || portfolio || !!orgId,
  );
  // A fresh list is the truth: a gate shown again (a second diagnosis still owed) is
  // workable again, so the "Done" marks last only until the list re-reads.
  useEffect(() => { setDone({}); }, [q.data]);
  const fee = usePerfApi((c, signal) => feeSchedule(c, { signal }), []);
  const p = q.data;
  const now = Date.now();

  const wrap = (children: React.ReactNode) => (
    <div data-screen-label="Rebates / Reimbursement" data-testid="perf-rebates" style={sx('position:relative;display:flex;flex-direction:column;gap:3px;min-width:0;flex:1 1 auto;min-height:0;height:100%;overflow-x:hidden;overflow-y:auto;')} aria-busy={q.loading && !p ? 'true' : undefined}>
      {children}
    </div>
  );

  const scopes = op
    ? (
        [
          ['bldg', 'This building'],
          ['portfolio', 'Portfolio'],
        ] as const
      ).map(([k, label]) => (
        <a
          key={k}
          href="#"
          aria-pressed={scope === k}
          onClick={(e) => {
            pre(e);
            setScope(k);
          }}
          style={sx('display:inline-flex;align-items:center;height:21px;padding:0 10px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;border-radius:3px;background:transparent;' + (scope === k ? 'color:' + INK + ';' : 'color:' + GREY + ';'))}
        >
          {label}
        </a>
      ))
    : null;

  if (!p) {
    return wrap(
      <>
        <div style={sx('flex:0 0 auto;border-top:1px solid rgba(34,39,46,.14);padding:5px 0 4px;min-width:0;')}>
          <div style={sx('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:4px;min-width:0;')}>
            <span style={sx('font-size:16px;font-weight:800;letter-spacing:-.01em;color:#22272E;white-space:nowrap;')}>Attribution</span>
            <span style={sx('flex:1 1 20px;')} />
            {scopes}
          </div>
        </div>
        {q.error ? (
          <div role="alert" style={sx('font-size:16px;color:' + (notServed(q.error) ? GREY : RED) + ';padding:8px 0;')}>
            {notServed(q.error) ? plain('The portfolio roll-up is not served yet.') : q.error.message}
          </div>
        ) : (
          <div style={sx('font-size:16px;color:' + GREY + ';padding:8px 0;')}>{fac || fa ? 'Reading the reimbursement record…' : 'Choose a building.'}</div>
        )}
      </>,
    );
  }

  const L = p.leakage;
  const A = p.attribution;
  const bases = p.bases ?? {};
  const stage = (key: string) => p.pipeline.stages.find((s) => s.key === key)?.residents ?? null;
  const eligible = stage('opportunity');
  const enrolled = stage('enroll');
  const booked = L.capturedNetMonthly;
  const recovered = A?.recoveredMonthly ?? 0;
  const created = A?.createdMonthly ?? 0;
  const open = L.leakageMonthly;
  const attrTag = tagOf(bases.attribution);

  const live = p.goLiveDate
    ? monthsLive(p.goLiveDate, now) + ' months live · ' + (() => {
        const d = new Date(p.goLiveDate + (p.goLiveDate.length === 10 ? 'T12:00:00' : ''));
        return MONTHS[d.getMonth()] + ' ' + d.getDate() + ' ' + d.getFullYear();
      })() + ' · ' + (p.historyIngested ? 'prior claims in' : 'prior claims not in')
    : 'no go-live date on record';
  const pillars: [string, string, ReturnType<typeof tagOf>, string, string][] = [
    ['Prior', A ? k$(A.baselineMonthly) : '–', attrTag, 'what this building billed monthly before go-live', GREY],
    ['Recovered', A ? k$(recovered) : '–', attrTag, 'their own qualifying work, now actually collected', GREEN],
    ['Created', A ? k$(created) : '–', attrTag, 'rails that did not exist here before us', GREEN],
    ['Still on the table', k$(open), tagOf(bases.leakage), 'eligible, not yet earned: not revenue', GREY],
  ];
  const attrNote = A
    ? 'Split per code, not by ratio. Modeled money is never added to earned.'
    : p.goLiveDate == null
      ? 'No go-live date is recorded for this facility, so there is no before and after to compare.'
      : 'No pre-go-live claims have been ingested. Load 3 to 6 months of prior claims to show what changed because of Aithora.';

  const money: [string, string, ReturnType<typeof tagOf>][] = [
    ['Capturable ceiling', k$(L.capturableMonthly), tagOf(bases.capturable)],
    ['Capture rate', (L.captureRate * 100).toFixed(1) + '%', tagOf(bases.captureRate)],
    ['Leakage', k$(open), tagOf(bases.leakage)],
    ['Per resident / mo', L.residents ? '$' + (booked / L.residents).toFixed(2) : '–', tagOf(bases.perResident)],
    ['Year to date', L.ytdCapturedNet != null ? k$(L.ytdCapturedNet) : '–', tagOf(bases.ytd)],
  ];

  const mix = p.payerMix;
  // A share too narrow to hold its number shows no number in the bar; the legend below always carries it.
  const pctOf = (v: number) => Math.round(v * 100) + '%';
  const inBar = (v: number) => (v * 100 >= 12 ? pctOf(v) : '');
  const payer = mix
    ? [seg(mix.medicare * 100, BLUE, inBar(mix.medicare)), seg(mix.medicaid * 100, 'rgba(34,39,46,.30)', inBar(mix.medicaid)), seg(mix.private * 100, 'rgba(34,39,46,.14)', inBar(mix.private), true)]
    : [];
  const funnel: [string, number | null, string][] = [
    ['Residents', L.residents, INK],
    ['Eligible', eligible, BLUE],
    ['Enrolled', enrolled, GREEN],
    ['Part A suppressed', L.partAFeed === false ? null : L.partASuppressed, GREY],
  ];

  const CLAIM_KEYS: [string, string][] = [
    ['draft', 'Draft'],
    ['ready', 'Ready'],
    ['submitted', 'Submitted'],
    ['paid', 'Paid'],
    ['denied', 'Denied'],
  ];
  const claimOf = (key: string) => p.claims.stages.find((s) => s.key === key) ?? { count: 0, amount: 0 };
  const nothingSubmitted = ['submitted', 'paid', 'denied'].every((k) => !claimOf(k).count);

  const who = (pr: PortalLiveProgram) => (pr.mode === 'detect' ? 'Facility performs' : pr.mode === 'none' ? 'Not billable · ' + (pr.blockedReason ?? 'no rail') : 'Aithora captures');
  const prog = p.programs.live
    .map((pr) => {
      const gap = pr.eligible != null ? Math.max(0, pr.eligible - pr.enrolled) : null;
      return { pr, gap, gapAmt: gap != null ? gap * pr.rate : 0 };
    })
    .sort((a, b) => b.gapAmt - a.gapAmt);
  const openTotal = prog.reduce((t, x) => t + x.gapAmt, 0);
  const anyEligible = prog.some((x) => x.gap != null);

  const totals = p.readinessTotals;
  const gateSum = totals ? totals.residents + ' residents · ' + k$(totals.blockedMonthly) + ' / mo blocked' : '';

  const curveCeiling = L.capturableMonthly || 1;
  const monthly = p.monthly ?? [];
  const H = 44;

  const pace = p.pace;
  const paceRows: [string, string | null][] = pace
    ? [
        ['Days to first claim', pace.daysToFirstClaim != null ? String(pace.daysToFirstClaim) : null],
        ['Enrollments / wk', pace.enrollmentsPerWeek != null ? pace.enrollmentsPerWeek.toFixed(1) : null],
        ['Denial rate', pace.denialRate != null ? (pace.denialRate * 100).toFixed(1) + '%' : null],
      ]
    : [];

  const sch = fee.data?.schedule;
  const exp = sch?.expiresOn ? (() => { const [y, m, d] = sch.expiresOn!.split('-').map(Number); return m + '/' + d + '/' + y; })() : null;
  const defend: [string, string][] = [
    ['Fee schedule', sch ? 'CY' + sch.year + ' · conversion factor $' + sch.conversionFactor.toFixed(4) + ' ' + sch.cfBasis + (exp ? ' · expires ' + exp : '') : '–'],
    ['Rates', fee.data ? fee.data.verified + ' of ' + fee.data.total + ' verified' : '–'],
    ['Consent on file', enrolled != null && eligible != null ? enrolled + ' of ' + eligible : '–'],
    ['Scope', portfolio ? 'portfolio roll-up' : fac?.name ?? '–'],
  ];

  const act = async (i: string, claimId: string, action: CaptureActionKey) => {
    setErr(null);
    try {
      await captureAction(client, { body: { action, claimIds: [claimId], reason: 'readiness' } });
      setDone((d) => ({ ...d, [i]: true }));
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  return wrap(
    <>
      {fa && (
        <div style={sx('box-sizing:border-box;display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;row-gap:4px;min-width:0;border-top:2px solid ' + INK + ';padding:6px 0 7px;')}>
          <span style={sx(DASH)}>YOUR BUILDING</span>
          <span style={sx('font-size:14px;color:#3C454F;line-height:1.4;min-width:0;text-wrap:pretty;')}>This building only. Peers are a percentile, never a name.</span>
        </div>
      )}

      <div style={sx('flex:0 0 auto;border-top:1px solid rgba(34,39,46,.14);padding:5px 0 4px;min-width:0;')} data-testid="rebates-attribution">
        <div style={sx('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:4px;min-width:0;')}>
          <span style={sx('font-size:16px;font-weight:800;letter-spacing:-.01em;color:#22272E;white-space:nowrap;')}>Attribution</span>
          <span style={sx('font-size:14px;color:#54606C;')}>
            <span>{live}</span>
          </span>
          <span style={sx('flex:1 1 20px;')} />
          {scopes}
        </div>
        <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(168px,1fr));gap:3px 18px;margin-top:5px;min-width:0;')}>
          {pillars.map(([k, v, t, means, c]) => (
            <div key={k} style={sx('min-width:0;box-sizing:border-box;padding:1px 0 2px;')}>
              <div style={sx('display:flex;align-items:baseline;gap:6px;flex-wrap:wrap;row-gap:2px;min-width:0;')}>
                <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;font-weight:700;color:#54606C;')}>
                  <span>{k}</span>
                </span>
                {t && (
                  <BuildTagged tag={t.tag}><span style={sx(t.style)}>
                    <span>{t.tag}</span>
                  </span></BuildTagged>
                )}
              </div>
              <div style={sx('font-size:19px;font-weight:800;letter-spacing:-.035em;line-height:1.05;margin-top:1px;font-variant-numeric:tabular-nums;color:' + c + ';')}>
                <span>{v}</span>
              </div>
              <div style={sx('font-size:14px;color:#3C454F;line-height:1.3;margin-top:1px;text-wrap:pretty;')}>
                <span>{means}</span>
              </div>
            </div>
          ))}
        </div>
        <div style={sx('display:flex;height:16px;margin-top:5px;border-radius:3px;overflow:hidden;background:rgba(34,39,46,.06);min-width:0;')}>
          {(A ? [seg(recovered, GREEN, 'Recovered ' + k$(recovered)), seg(created, '#2F7A5E', 'Created ' + k$(created)), seg(open, 'rgba(34,39,46,.16)', 'Still on the table ' + k$(open), true)] : [seg(open, 'rgba(34,39,46,.16)', 'Still on the table ' + k$(open), true)]).map((b) => (
            <div key={b.lab} style={sx(b.segStyle)}>
              <span style={sx(b.labStyle)}>
                <span>{b.lab}</span>
              </span>
            </div>
          ))}
        </div>
        <div style={sx('display:flex;align-items:baseline;gap:14px;flex-wrap:wrap;row-gap:3px;margin-top:5px;min-width:0;')}>
          <span style={sx('font-size:14px;font-weight:800;color:#1F6B52;white-space:nowrap;')}>
            <span>{A ? 'Net lift +' + k$(A.liftMonthly) + ' / mo' : ''}</span>
          </span>
          <span style={sx('font-size:14px;color:#54606C;line-height:1.35;min-width:0;text-wrap:pretty;')}>
            <span>{attrNote}</span>
          </span>
        </div>
      </div>

      {err && (
        <div role="alert" style={sx('font-size:16px;color:' + RED + ';')}>
          {err}
        </div>
      )}

      <div style={sx('flex:0 0 auto;display:grid;grid-template-columns:repeat(auto-fit,minmax(296px,1fr));gap:9px 16px;min-width:0;')}>
        <div style={sx(CARD)} data-testid="rebates-money">
          <div style={sx(CARD_HEAD)}>The money, this period</div>
          <div style={sx('display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;row-gap:3px;margin-top:6px;min-width:0;')}>
            <span style={sx('font-size:21px;font-weight:800;letter-spacing:-.03em;line-height:1;color:#1F6B52;')}>
              <span>{k$(booked)}</span>
            </span>
            <span style={sx('font-size:14px;color:#54606C;line-height:1.3;min-width:0;')}>
              <span>{'net collectible / mo · gross billed ' + k$(L.capturedMonthly)}</span>
            </span>
          </div>
          <div style={sx('margin-top:9px;min-width:0;')}>
            {money.map(([k, v, t]) => (
              <div key={k} style={sx(reg('minmax(0,1fr) 62px auto'))}>
                <span style={sx(K_STYLE)}>
                  <span>{k}</span>
                </span>
                <span style={sx(num(INK, 16))}>
                  <span>{v}</span>
                </span>
                <BuildTagged tag={t?.tag} keepSlot>
                  <span style={sx(t ? t.style : '')}>
                    <span>{t ? t.tag : ''}</span>
                  </span>
                </BuildTagged>
              </div>
            ))}
          </div>
          <div style={sx(NOTE)}>Net collectible, not gross: the two differ ~20% on FFS, not at all for duals.</div>
        </div>

        <div style={sx(CARD)} data-testid="rebates-billable">
          <div style={sx(CARD_HEAD)}>Who is billable</div>
          <div style={sx('display:flex;height:22px;margin-top:7px;border-radius:3px;overflow:hidden;min-width:0;')}>
            {payer.map((b) => (
              <div key={b.segStyle} style={sx(b.segStyle)}>
                <span style={sx(b.labStyle)}>
                  <span>{b.lab}</span>
                </span>
              </div>
            ))}
          </div>
          <div style={sx('display:flex;flex-wrap:wrap;gap:3px 12px;margin-top:5px;min-width:0;')}>
            {mix ? (
              (
                [
                  ['Medicare FFS + MA · payable', BLUE, mix.medicare],
                  ['Medicaid · not payable', 'rgba(34,39,46,.30)', mix.medicaid],
                  ['Private · self-pay', 'rgba(34,39,46,.14)', mix.private],
                ] as const
              ).map(([label, c, v]) => (
                <span key={label} style={sx('display:inline-flex;align-items:center;gap:5px;font-size:14px;color:' + GREY + ';white-space:nowrap;')}>
                  <i style={sx('display:inline-block;width:6px;height:6px;border-radius:2px;background:' + c + ';')} />
                  <span>{label}</span>
                  <b style={sx('font-weight:700;color:' + INK + ';font-variant-numeric:tabular-nums;')}>{pctOf(v)}</b>
                </span>
              ))
            ) : (
              <span style={sx('font-size:14px;color:' + GREY + ';')}>Payer mix not served yet.</span>
            )}
          </div>
          <div style={sx('margin-top:8px;min-width:0;')}>
            {funnel.map(([k, v, c]) => (
              <div key={k} style={sx(reg('minmax(0,1fr) minmax(0,.8fr) auto') + (v === null ? 'opacity:.7;' : ''))}>
                <span style={sx(K_STYLE)}>
                  <span>{k}</span>
                </span>
                <span style={sx('display:block;height:7px;border-radius:2px;background:rgba(34,39,46,.07);overflow:hidden;')}>
                  <i style={sx('display:block;height:7px;border-radius:2px;background:' + c + ';width:' + (v === null || !L.residents ? 0 : Math.round((v / L.residents) * 100)) + '%;')} />
                </span>
                <span style={sx(v === null ? 'font-size:14px;color:' + GREY + ';white-space:nowrap;text-align:right;' : num(c, 16))}>
                  <span>{v === null ? 'needs census feed' : String(v)}</span>
                </span>
              </div>
            ))}
          </div>
          <div style={sx(NOTE)}>Medicaid and MA do not pay these separately. Part A suppression is compliance, not a miss.</div>
        </div>

        <div style={sx(CARD)} data-testid="rebates-claims">
          <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:3px;min-width:0;')}>
            <span style={sx(CARD_HEAD)}>Claims</span>
            <span style={sx('flex:1 1 10px;')} />
            <a
              href="#"
              aria-disabled={!p.claims.auditBundleAvailable}
              onClick={(e) => {
                pre(e);
                if (p.claims.auditBundleAvailable) navigate('/capture');
              }}
              style={sx('font-size:14px;font-weight:700;color:' + BLUE + ';text-decoration:none;white-space:nowrap;border:1px solid rgba(34,39,46,.18);border-radius:5px;padding:2px 8px;')}
            >
              Audit bundle
            </a>
          </div>
          <div style={sx('margin-top:7px;min-width:0;')}>
            {CLAIM_KEYS.map(([key, label]) => {
              const c = claimOf(key);
              return (
                <div key={key} style={sx(reg('minmax(0,1fr) 34px 58px') + (c.count ? '' : 'opacity:.55;'))}>
                  <span style={sx(K_STYLE)}>
                    <span>{label}</span>
                  </span>
                  <span style={sx(num(c.count ? INK : GREY, 16))}>
                    <span>{String(c.count)}</span>
                  </span>
                  <span style={sx(num(c.count ? GREEN : GREY, 16))}>
                    <span>{c.count ? k$(c.amount) : '–'}</span>
                  </span>
                </div>
              );
            })}
          </div>
          {nothingSubmitted && (
            <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:4px;margin-top:8px;padding:6px 9px;min-width:0;border:1px dashed rgba(34,39,46,.22);border-radius:6px;background:rgba(34,39,46,.03);')}>
              <span style={sx(DASH)}>EMPTY PIPELINE</span>
              <span style={sx('font-size:14px;color:#3C454F;line-height:1.4;min-width:0;text-wrap:pretty;')}>Nothing submitted yet, so days-to-payment and denials hold no value. Empty, not failing.</span>
            </div>
          )}
        </div>
      </div>

      <div style={sx('flex:0 0 auto;display:grid;grid-template-columns:repeat(auto-fit,minmax(286px,1fr));gap:9px 16px;min-width:0;')}>
        <div style={sx('border-top:1px solid rgba(34,39,46,.14);padding:8px 0 0;min-width:0;')} data-testid="rebates-programs">
          <div style={sx('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:3px;min-width:0;')}>
            <span style={sx('font-size:16px;font-weight:800;letter-spacing:-.01em;color:#22272E;')}>Per program</span>
            <span style={sx('flex:1 1 10px;')} />
            <span style={sx('font-size:14px;font-weight:800;color:#2F5C97;white-space:nowrap;')}>
              <span>{anyEligible ? '+' + k$(openTotal) + ' / mo eligible, not enrolled' : ''}</span>
            </span>
          </div>
          <div style={sx('margin-top:5px;min-width:0;')}>
            {prog.map(({ pr, gap, gapAmt }) => (
              <div key={pr.code} style={sx('padding:2px 0;min-width:0;border-top:1px solid rgba(34,39,46,.07);' + (pr.monthly ? '' : 'opacity:.6;'))}>
                <span style={sx('display:flex;align-items:baseline;gap:6px;min-width:0;')}>
                  <span style={sx('font-size:14px;font-weight:800;font-variant-numeric:tabular-nums;color:' + INK + ';white-space:nowrap;')}>
                    <span>{pr.code}</span>
                  </span>
                  <span style={sx('flex:1 1 auto;min-width:0;font-size:14px;color:' + SOFT + ';line-height:1.2;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;')}>
                    <span>{pr.label}</span>
                  </span>
                  <span style={sx(num(pr.monthly ? GREEN : GREY, 16))}>
                    <span>{pr.monthly ? k$(pr.monthly) : '–'}</span>
                  </span>
                </span>
                <span style={sx('display:flex;align-items:baseline;gap:6px;min-width:0;margin-top:1px;')}>
                  <BuildTagged tag={pr.rateVerified ? 'VERIFIED' : 'ESTIMATED'}>
                    <span style={sx((pr.rateVerified ? OURS : DASH) + 'flex:0 0 auto;')}>
                      <span>{pr.rateVerified ? 'VERIFIED' : 'ESTIMATED'}</span>
                    </span>
                  </BuildTagged>
                  <span style={sx('min-width:0;font-size:14px;color:' + GREY + ';white-space:nowrap;overflow:hidden;text-overflow:ellipsis;')}>
                    <span>{'$' + pr.rate + ' · ' + (pr.enrolled ? pr.enrolled + ' enrolled' : 'none enrolled') + ' · ' + who(pr)}</span>
                  </span>
                  <span style={sx('flex:1 1 4px;')} />
                  <span style={sx(num(GREY, 14))}>
                    <span>{gapAmt ? '+' + k$(gapAmt) + ' · ' + gap + ' more' : '–'}</span>
                  </span>
                </span>
              </div>
            ))}
          </div>
          <div style={sx(NOTE)}>{'Not-enrolled is modeled eligibility · ' + (fee.data ? fee.data.verified + ' of ' + fee.data.total : '–') + ' rates verified.'}</div>
        </div>

        <div style={sx('border-top:1px solid rgba(34,39,46,.14);padding:8px 0 0;min-width:0;')} data-testid="rebates-readiness">
          <div style={sx('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:3px;min-width:0;')}>
            <span style={sx('font-size:16px;font-weight:800;letter-spacing:-.01em;color:#22272E;')}>Readiness · work this list</span>
            <span style={sx('flex:1 1 10px;')} />
            <span style={sx('font-size:14px;font-weight:800;color:#B3261E;white-space:nowrap;')}>
              <span>{gateSum}</span>
            </span>
          </div>
          <div style={sx('display:flex;flex-wrap:wrap;gap:5px;margin-top:6px;min-width:0;')}>
            {p.pipeline.needAction.map((g) => (
              <span key={g.key} style={sx('font-size:14px;font-weight:700;color:' + SOFT + ';background:rgba(34,39,46,.06);border-radius:3px;padding:2px 7px;white-space:nowrap;')}>
                <span>{g.label + ' ' + g.residents}</span>
              </span>
            ))}
          </div>
          <div style={sx('margin-top:7px;min-width:0;')}>
            {p.readiness.slice(0, 6).map((w, i) => {
              const action = ACTION[w.actionRoute] ?? ACTION[w.blocker];
              // Production's fix sheet: a gate with a record to write opens it (no claim needed).
              const fixable = FIXABLE.has(w.blocker) && !portfolio;
              const dk = doneKey(w);
              const can = canAct && !done[dk] && (fixable || (!!w.claimId && !!action));
              const title = roReason ?? (!canAct ? READ_ONLY_SHORT : !w.claimId ? 'No claim to record this against yet.' : undefined);
              return (
                <div key={w.residentId + i} style={sx('display:grid;grid-template-columns:minmax(0,.75fr) minmax(0,1fr) auto minmax(0,150px);gap:7px;align-items:center;padding:2px 0;min-width:0;border-top:1px solid rgba(34,39,46,.07);')} data-testid="rebates-work">
                  <span style={sx('min-width:0;font-size:14px;font-weight:700;color:' + INK + ';overflow-wrap:anywhere;')}>
                    <span>{w.resident}</span>
                  </span>
                  <span style={sx('min-width:0;font-size:14px;color:' + SOFT + ';line-height:1.25;')}>
                    <span>{w.blockerLabel}</span>
                  </span>
                  <span style={sx(num(RED, 16))}>
                    <span>{k$(w.blockedNetMonthly || w.blockedMonthly)}</span>
                  </span>
                  <a
                    href="#"
                    aria-disabled={!can}
                    title={title}
                    onClick={(e) => {
                      pre(e);
                      if (!can) return;
                      if (fixable) setFixFor({ i, row: w });
                      else void act(dk, w.claimId!, action!);
                    }}
                    style={sx('display:inline-flex;align-items:center;justify-content:center;min-height:22px;padding:3px 9px;font-size:14px;font-weight:700;line-height:1.2;text-align:center;text-decoration:none;border-radius:4px;background:' + INK + ';color:#FCFAF4;' + (done[dk] ? 'opacity:.6;' : !can ? 'opacity:.55;cursor:not-allowed;' : ''))}
                  >
                    <span>{done[dk] ? 'Done' : w.action}</span>
                  </a>
                </div>
              );
            })}
            {!p.readiness.length && <div style={sx('font-size:14px;color:' + GREY + ';padding:4px 0;')}>No resident is blocked by a gate right now.</div>}
          </div>
          <div style={sx(NOTE)}>One gate per resident: the first that stops the claim. The button clears it.</div>
          {fixFor && (
            <FixSheet
              client={client}
              row={fixFor.row}
              onDone={() => { setDone((d) => ({ ...d, [doneKey(fixFor.row)]: true })); q.reload(); }}
              onClose={() => setFixFor(null)}
            />
          )}
        </div>

        <div style={sx('border-top:1px solid rgba(34,39,46,.14);padding:8px 0 0;min-width:0;')} data-testid="rebates-rails">
          <div style={sx('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:3px;min-width:0;')}>
            <span style={sx('font-size:16px;font-weight:800;letter-spacing:-.01em;color:#22272E;')}>Not on a billing rail</span>
            <span style={sx('font-size:14px;color:#54606C;')}>status only · no dollars booked</span>
          </div>
          <div style={sx('display:grid;grid-template-columns:minmax(0,1fr);gap:5px;margin-top:6px;min-width:0;')}>
            {p.programs.building.map((b) => (
              <div key={b.key} style={sx('box-sizing:border-box;border:1px dashed rgba(34,39,46,.24);border-radius:7px;padding:5px 9px 6px;min-width:0;background:rgba(34,39,46,.03);')}>
                <div style={sx('display:flex;align-items:baseline;gap:7px;flex-wrap:wrap;row-gap:2px;min-width:0;')}>
                  <span style={sx('font-size:14px;font-weight:800;color:#22272E;min-width:0;')}>
                    <span>{b.label}</span>
                  </span>
                  <span style={sx(DASH)}>
                    <span>{b.statusLabel.toUpperCase()}</span>
                  </span>
                </div>
                {b.estimateAnnualUpTo != null && (
                  <div style={sx('font-size:16px;font-weight:800;letter-spacing:-.02em;color:' + GREY + ';margin-top:4px;')}>
                    <span>{'up to ' + k$(b.estimateAnnualUpTo)}</span>
                  </div>
                )}
                <div style={sx('font-size:14px;color:#54606C;line-height:1.35;margin-top:2px;text-wrap:pretty;')}>
                  <span>{b.note}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={sx('flex:0 0 auto;display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:7px 16px;min-width:0;')}>
        <div style={sx('grid-column:span 2;border-top:1px solid rgba(34,39,46,.14);padding:8px 0 0;min-width:0;')} data-testid="rebates-curve">
          <div style={sx('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:2px;min-width:0;')}>
            <span style={sx('font-size:16px;font-weight:800;letter-spacing:-.01em;color:#22272E;')}>Capture since go-live</span>
            <span style={sx('font-size:14px;color:#54606C;min-width:0;')}>bar = booked · outline = modeled ceiling</span>
          </div>
          <div style={sx('display:flex;align-items:flex-end;gap:5px;margin-top:4px;min-width:0;')}>
            {monthly.map((m, i) => {
              const v = Math.round(m.bookedMonthly);
              const h = Math.max(2, Math.round((v / curveCeiling) * H));
              return (
                <span key={m.period} style={sx('flex:1 1 0;min-width:0;display:flex;flex-direction:column;justify-content:flex-end;align-items:stretch;gap:1px;')}>
                  <span style={sx('font-size:14px;font-weight:800;font-variant-numeric:tabular-nums;text-align:center;white-space:nowrap;color:' + (v ? GREEN : GREY) + ';')}>
                    <span>{v ? k$(v) : '–'}</span>
                  </span>
                  <span style={sx('position:relative;display:flex;align-items:flex-end;flex:0 0 ' + H + 'px;height:' + H + 'px;border-top:1px dashed rgba(34,39,46,.26);box-sizing:border-box;')}>
                    <i style={sx('display:block;width:100%;border-radius:1px 1px 0 0;background:' + (i === monthly.length - 1 ? GREEN : 'rgba(47,138,111,.55)') + ';height:' + Math.min(H, h) + 'px;animation:growH .5s cubic-bezier(.22,.61,.36,1);transform-origin:bottom;')} />
                  </span>
                  <span style={sx('font-size:14px;color:' + GREY + ';text-align:center;')}>
                    <span>{MONTHS[Number(m.period.slice(5, 7)) - 1] ?? m.period}</span>
                  </span>
                </span>
              );
            })}
            {!monthly.length && <span style={sx('font-size:14px;color:' + GREY + ';padding:4px 0;')}>The month-by-month record is not served yet.</span>}
          </div>
          <div style={sx(NOTE)}>Booked per month is our billing record; the dashed ceiling is the same modeled figure as the hero.</div>
        </div>

        <div style={sx('border-top:1px solid rgba(34,39,46,.14);padding:8px 0 0;min-width:0;')}>
          <div style={sx('font-size:16px;font-weight:800;letter-spacing:-.01em;color:#22272E;')}>Capability or capture</div>
          <div style={sx('display:flex;align-items:baseline;gap:7px;flex-wrap:wrap;row-gap:2px;margin-top:6px;min-width:0;')}>
            <span style={sx('font-size:23px;font-weight:800;letter-spacing:-.035em;line-height:1;color:#22272E;font-variant-numeric:tabular-nums;')}>
              <span>{A ? (recovered / (created || 1)).toFixed(2) + ':1' : '–'}</span>
            </span>
            {attrTag && A && (
              <BuildTagged tag={attrTag.tag}><span style={sx(attrTag.style)}>
                <span>{attrTag.tag}</span>
              </span></BuildTagged>
            )}
          </div>
          <div style={sx(NOTE)}>
            {A
              ? 'Recovered to created. Above 1 means this building mostly needed capture, not capability: ' + k$(recovered) + ' was already theirs to bill.'
              : 'Recovered to created needs the pre-go-live claims.'}
          </div>
        </div>

        <div style={sx('border-top:1px solid rgba(34,39,46,.14);padding:8px 0 0;min-width:0;')}>
          <div style={sx('font-size:16px;font-weight:800;letter-spacing:-.01em;color:#22272E;')}>Onboarding pace</div>
          <div style={sx('margin-top:5px;min-width:0;')}>
            {paceRows.map(([k, v]) => (
              <div key={k} style={sx(reg('minmax(0,1fr) auto') + (v === null ? 'opacity:.7;' : ''))}>
                <span style={sx(K_STYLE)}>
                  <span>{k}</span>
                </span>
                <span style={sx(v === null ? 'font-size:14px;color:' + GREY + ';white-space:nowrap;text-align:right;' : num(INK, 16))}>
                  <span>{v ?? (k === 'Denial rate' ? 'no claims yet' : '–')}</span>
                </span>
              </div>
            ))}
            {!pace && <div style={sx('font-size:14px;color:' + GREY + ';padding:4px 0;')}>Onboarding pace is not served yet.</div>}
          </div>
          <div style={sx(NOTE)}>Denial rate has no value until a claim is adjudicated: empty, not zero. Peer position lives on the benchmark page.</div>
        </div>
      </div>

      <div style={sx('flex:0 0 auto;border-top:1px solid rgba(34,39,46,.12);padding-top:2px;display:flex;align-items:baseline;gap:14px;flex-wrap:wrap;row-gap:3px;min-width:0;')}>
        {defend.map(([k, v]) => (
          <span key={k} style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>
            <b style={sx('color:#3C454F;font-weight:700;')}>
              <span>{k}</span>
            </b>{' '}
            <span>{v}</span>
          </span>
        ))}
        <span style={sx('font-size:14px;color:#54606C;min-width:0;text-wrap:pretty;')}>Aithora documents; the credentialed practitioner bills.</span>
      </div>
    </>,
  );
}
